import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import { HismithSettingItem } from './useHismith';
import useSubscriber from './useSubscriber';

const useHismithSettings = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);

    const base = (): string => `${settingsContext.routes.hismith.base}${subscriber._id}/settings`;

    const fetchData = async (): Promise<HismithSettingItem[] | null> =>
        axios
            .get(base())
            .then(response => response.data)
            .catch(() => null);

    const settings = useQuery(['hismithSettings', subscriber?._id], () => fetchData(), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id ? true : false,
    });

    const saveSetting = async (item: HismithSettingItem): Promise<{ success: boolean; settingId?: string }> => {
        setIsUpdatingSettings(true);

        return axios
            .post(base(), item)
            .then(response => {
                settings.refetch();

                return response.data;
            })
            .catch(error => {
                throw new Error(error?.response?.data?.message || error?.response?.data || error.message || 'Failed to save tip range.');
            })
            .finally(() => setIsUpdatingSettings(false));
    };

    const deleteSetting = async (settingId: string): Promise<{ success: boolean }> => {
        setIsUpdatingSettings(true);

        return axios
            .delete(`${base()}/${settingId}`)
            .then(response => {
                settings.refetch();

                return response.data;
            })
            .catch(error => {
                throw new Error(error?.response?.data?.message || error?.response?.data || error.message || 'Failed to delete tip range.');
            })
            .finally(() => setIsUpdatingSettings(false));
    };

    return {
        ...settings,
        isUpdatingSettings,
        saveSetting,
        deleteSetting,
    };
};

export default useHismithSettings;
